import type {
  CyberAssessmentCmmcDomainSummarySelectDto,
  CyberAssessmentCmmcSelectDto,
  CyberAssessmentOverviewSnapshotSelectDto,
  TechnologyType,
} from '@@types/api.types';
import {
  CyberAssessmentAukusReadinessBand,
  CyberAssessmentClassificationCeiling,
  CyberAssessmentCpiBandLabel,
  CyberAssessmentDeploymentReadinessBand,
  CyberAssessmentPriority,
  CyberAssessmentRagStatus,
} from '@azymmetric/shared/types/cyber-assessment.types';
import { formatDateUtc } from '@utils/date.util';

type CmmcIndicativeLevelPresentation = {
  badgeClassName: string;
  description: string;
};

type CmmcDisplayCountsSource = Pick<
  CyberAssessmentCmmcDomainSummarySelectDto,
  'practicesGapMapped' | 'practicesNotAssessed' | 'practicesPartial' | 'practicesPartiallyMapped'
>;

type CyberAssessmentOverviewBadgeValue =
  | CyberAssessmentAukusReadinessBand
  | CyberAssessmentClassificationCeiling
  | CyberAssessmentCmmcSelectDto['indicativeLevel']
  | CyberAssessmentCpiBandLabel
  | CyberAssessmentDeploymentReadinessBand
  | CyberAssessmentOverviewSnapshotSelectDto['cpiBandLabel']
  | CyberAssessmentOverviewSnapshotSelectDto['deploymentReadinessBand'];

const CMMC_COVERAGE_WARNING_THRESHOLD = 75;
const CMMC_INDICATIVE_LEVEL_PRESENTATION_BY_VALUE: Record<string, CmmcIndicativeLevelPresentation> = {
  'Below Level 2': {
    badgeClassName: 'border border-rose-200 bg-rose-50 text-rose-700',
    description: 'Baseline CMMC controls are partially established, but material Level 2 gaps remain.',
  },
  'Level 2 Ready': {
    badgeClassName: 'border border-amber-200 bg-amber-50 text-amber-700',
    description: 'All mapped Level 2 practices are satisfied. Level 3 enhancements still remain open.',
  },
  'Level 3 Ready': {
    badgeClassName: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
    description: 'All mapped Level 2 and Level 3 practices are satisfied for the target profile.',
  },
};
const DEFAULT_CMMC_INDICATIVE_LEVEL_PRESENTATION: CmmcIndicativeLevelPresentation = {
  badgeClassName: 'border border-rose-200 bg-rose-50 text-rose-700',
  description:
    'Current CMMC coverage is indicative only and may still reflect scope or SCRDF crosswalk coverage limits.',
};
const CRITICAL_OVERVIEW_BADGE_VALUES = new Set<CyberAssessmentOverviewBadgeValue>([
  'Below Level 2',
  CyberAssessmentAukusReadinessBand.NOT_READY,
]);
const SUCCESS_OVERVIEW_BADGE_VALUES = new Set<CyberAssessmentOverviewBadgeValue>([
  CyberAssessmentAukusReadinessBand.READY,
  CyberAssessmentDeploymentReadinessBand.CLEARED,
]);
const WARNING_OVERVIEW_BADGE_VALUES = new Set<CyberAssessmentOverviewBadgeValue>([
  CyberAssessmentAukusReadinessBand.CONDITIONAL,
  CyberAssessmentAukusReadinessBand.PARTIAL,
  CyberAssessmentDeploymentReadinessBand.CONDITIONAL,
  CyberAssessmentDeploymentReadinessBand.PROVISIONAL,
]);
const SKY_OVERVIEW_BADGE_VALUES = new Set<CyberAssessmentOverviewBadgeValue>([
  CyberAssessmentClassificationCeiling.CUI,
  CyberAssessmentClassificationCeiling.OFFICIAL,
  CyberAssessmentClassificationCeiling.OFFICIAL_SENSITIVE,
  CyberAssessmentClassificationCeiling.PROTECTED,
  CyberAssessmentClassificationCeiling.SECRET,
  CyberAssessmentClassificationCeiling.TOP_SECRET,
  CyberAssessmentClassificationCeiling.TS_SCI,
]);
const RAG_BADGE_VARIANT_BY_STATUS = {
  GREEN: 'success',
  RED: 'destructive',
  YELLOW: 'warning',
} as const;

export const formatClassificationBadgeLabel = (value: string): string => {
  return value
    .toLowerCase()
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

export const formatCyberAssessmentTechnologyTypeLabel = (technologyType: TechnologyType): string => {
  return technologyType.charAt(0) + technologyType.slice(1).toLowerCase();
};

export const formatCyberAssessmentTechnologyTypesLabel = (technologyTypes: TechnologyType[]): string => {
  return technologyTypes.map(formatCyberAssessmentTechnologyTypeLabel).join(' + ');
};

export const formatCyberAssessmentLabel = (params: {
  createdAt: string;
  technologyName: string;
  technologyTypes: TechnologyType[];
}): string => {
  const { createdAt, technologyName, technologyTypes } = params;

  return `${technologyName} • ${formatCyberAssessmentTechnologyTypesLabel(technologyTypes)} • ${formatDateUtc(createdAt)}`;
};

export const formatActionPlanPhaseBadgeLabel = (phaseTitle: string): string => {
  const normalizedPhaseTitle = phaseTitle.replace(/^Phase\s+\d+\s*:\s*/i, '').trim();

  return normalizedPhaseTitle || phaseTitle;
};

export const formatCmmcGapPriorityItemLabel = (params: {
  practiceId: string;
  practiceName: string;
  questionIds: string[];
}): string => {
  const { practiceId, practiceName, questionIds } = params;
  const questionIdsLabel = questionIds.join(', ');

  return `[${practiceId}] ${practiceName} (SCRDF: ${questionIdsLabel})`;
};

export const formatCmmcGapPriorityBadgeLabel = (value: number | string): string => {
  return String(value);
};

export const getActionPlanQuestionBadgeClassName = (priority: string): string => {
  if (priority === CyberAssessmentPriority.CRITICAL) {
    return 'border border-rose-200 bg-rose-100 text-rose-700';
  }

  if (priority === CyberAssessmentPriority.HIGH) {
    return 'border border-amber-200 bg-amber-100 text-amber-700';
  }

  return 'border border-emerald-200 bg-emerald-100 text-emerald-700';
};

export const getCmmcCoverageToneClassName = (percentage: number): string => {
  if (percentage >= 100) {
    return 'text-emerald-600';
  }

  if (percentage >= CMMC_COVERAGE_WARNING_THRESHOLD) {
    return 'text-amber-600';
  }

  return 'text-rose-600';
};

export const getCmmcIndicativeLevelPresentation = (
  indicativeLevel: CyberAssessmentCmmcSelectDto['indicativeLevel'],
): CmmcIndicativeLevelPresentation => {
  return CMMC_INDICATIVE_LEVEL_PRESENTATION_BY_VALUE[indicativeLevel] ?? DEFAULT_CMMC_INDICATIVE_LEVEL_PRESENTATION;
};

export const getCmmcDomainDisplayCounts = (
  domainSummary: CmmcDisplayCountsSource,
): {
  notAssessedCount: number;
  partialCount: number;
} => {
  return {
    notAssessedCount: domainSummary.practicesGapMapped + domainSummary.practicesNotAssessed,
    partialCount: domainSummary.practicesPartial + domainSummary.practicesPartiallyMapped,
  };
};

export const getOverviewMetricBadgeClassName = (value: CyberAssessmentOverviewBadgeValue | string): string => {
  if (CRITICAL_OVERVIEW_BADGE_VALUES.has(value)) {
    return 'border border-rose-200 bg-rose-50 text-rose-700';
  }

  if (SUCCESS_OVERVIEW_BADGE_VALUES.has(value)) {
    return 'border border-emerald-200 bg-emerald-50 text-emerald-700';
  }

  if (WARNING_OVERVIEW_BADGE_VALUES.has(value)) {
    return 'border border-amber-200 bg-amber-50 text-amber-700';
  }

  if (SKY_OVERVIEW_BADGE_VALUES.has(value)) {
    return 'border border-sky-200 bg-sky-50 text-sky-700';
  }

  return 'border border-slate-200 bg-slate-50 text-slate-700';
};

export const resolveCyberQuestionRagBadgeVariant = (
  ragStatus: string,
): 'destructive' | 'secondary' | 'success' | 'warning' => {
  if (ragStatus === CyberAssessmentRagStatus.GREEN) {
    return RAG_BADGE_VARIANT_BY_STATUS.GREEN;
  }

  if (ragStatus === CyberAssessmentRagStatus.RED) {
    return RAG_BADGE_VARIANT_BY_STATUS.RED;
  }

  if (ragStatus === CyberAssessmentRagStatus.YELLOW) {
    return RAG_BADGE_VARIANT_BY_STATUS.YELLOW;
  }

  return 'secondary';
};
